import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Image,
  StyleSheet,
  Alert,
  ActivityIndicator,
  TouchableWithoutFeedback,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  SafeAreaView,
  Switch
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { storage, firestore, auth } from '../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  orderBy,
  getDocs,
  doc
} from 'firebase/firestore';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { translations as translationsData, Language } from '../config/translations';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { PriorityLevel, SafetyProblemType } from '../types/report';
import { Picker } from '@react-native-picker/picker';

interface Location {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  order: number;
}

interface Photo {
  url: string;
  uploadedAt: string;
  description: string | null;
  uri?: string; 
}

interface Translations {
  [key: string]: any;
  location: {
    label: string;
    placeholder: string;
  };
  createReport: {
    title: string;
    titleField: string;
    titlePlaceholder: string;
    description: string;
    takePhoto: string;
    choosePhoto: string;
    removePhoto: string;
    submit: string;
    success: string;
    error: {
      noTitle: string;
      noDescription: string;
      cameraPermission: string;
      photoPermission: string;
      uploadError: string;
      createError: string;
      noGroup: string;
    };
    buttons: {
      cancel: string;
      continue: string;
    };
    operationalImpact: {
      label: string;
      placeholder: string;
    };
    temporarySolution: {
      label: string;
      placeholder: string;
    };
    problemType: {
      label: string;
      [key: string]: string;
    };
  };
}

interface Report {
  description: string;
  photos: Photo[];
  photoUrl: string | null;
  groupId: string;
  createdBy: string | undefined;
  createdAt: any;
  status: 'new' | 'in_progress' | 'resolved' | 'pending_approval';
  operationalImpact: string;
  temporarySolution: string;
  problemType: SafetyProblemType | string;
  isCustomType: boolean;
  customProblemType: string | null;
  locationId: string | null;
  approvedBy: null;
  approvedAt: null;
  title: string;
}

export const CreateReportScreen = ({ navigation }: any) => {
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [language, setLanguage] = useState<Language>('en');
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    title: string;
    message: string;
    onConfirm?: () => void;
    showCancel?: boolean;
  }>({ title: '', message: '' });
  
  // New state variables
  const [operationalImpact, setOperationalImpact] = useState('');
  const [temporarySolution, setTemporarySolution] = useState('');
  const [problemType, setProblemType] = useState<SafetyProblemType | string>('other');
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [translations, setTranslations] = useState<Translations | null>(null);

  // Add isOnline state
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    loadLanguage();
    setupConnectivityListeners();
    const fetchLocations = async () => {
      try {
        const groupId = await AsyncStorage.getItem('currentGroupId');
        console.log('Fetching locations for groupId:', groupId);
        
        if (!groupId) {
          console.error('No groupId found in AsyncStorage');
          return;
        }

        const groupDocRef = doc(firestore, 'groups', groupId);
        const locationsRef = collection(groupDocRef, 'locations');
        console.log('Fetching from path:', `groups/${groupId}/locations`);
        
        const q = query(locationsRef, orderBy('order', 'asc'));
        const snapshot = await getDocs(q);
        
        console.log('Found locations:', snapshot.size);
        
        const locationsList = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log('Location data:', { id: doc.id, ...data });
          return {
            id: doc.id,
            name: data.name || '',
            description: data.description || '',
            isActive: data.isActive ?? true,
            order: data.order || 0
          };
        });
        
        const activeLocations = locationsList.filter(loc => loc.isActive);
        console.log('Active locations:', activeLocations);
        
        setLocations(activeLocations);
      } catch (error) {
        console.error('Error fetching locations:', error);
        Alert.alert(
          'Error',
          'Kan de locaties niet laden. Probeer de pagina te verversen.',
          [
            {
              text: 'OK',
              onPress: () => navigation.goBack()
            },
            {
              text: 'Opnieuw proberen',
              onPress: () => fetchLocations()
            }
          ]
        );
      }
    };

    fetchLocations();
  }, [navigation]);

  const loadLanguage = async () => {
    try {
      const savedLanguage = await AsyncStorage.getItem('selectedLanguage');
      if (savedLanguage) {
        setLanguage(savedLanguage as Language);
        setTranslations(translationsData[savedLanguage as Language]);
      } else {
        // Default to Dutch if no language is set
        setLanguage('nl');
        setTranslations(translationsData.nl);
      }
    } catch (error) {
      console.error('Error loading language:', error);
      // Set default translations to prevent null errors
      setTranslations(translationsData.nl);
    }
  };

  const setupConnectivityListeners = () => {
    // Check initial online status
    setIsOnline(window.navigator.onLine);

    // Add event listeners for online/offline status
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  };

  const handleOnline = () => {
    setIsOnline(true);
    showCustomAlert({
      title: 'Internet Verbinding Hersteld',
      message: 'Je bent weer online. Je kunt nu meldingen maken.',
      onConfirm: () => setShowAlert(false)
    });
  };

  const handleOffline = () => {
    setIsOnline(false);
    showCustomAlert({
      title: 'Geen Internet Verbinding',
      message: 'Je bent offline. Controleer je internetverbinding om meldingen te kunnen maken.',
      onConfirm: () => setShowAlert(false)
    });
  };

  const checkConnection = async (): Promise<boolean> => {
    if (!isOnline) {
      showCustomAlert({
        title: 'Geen Internet Verbinding',
        message: 'Je hebt geen internetverbinding. Controleer je verbinding en probeer het opnieuw.',
        onConfirm: () => setShowAlert(false)
      });
      return false;
    }
    return true;
  };

  const pickImage = async () => {
    if (photos.length >= 10) {
      Alert.alert('Maximum aantal foto\'s', 'Je kunt maximaal 10 foto\'s toevoegen.');
      return;
    }

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 1,
      });

      if (!result.canceled) {
        const manipResult = await manipulateAsync(
          result.assets[0].uri,
          [],
          { compress: 0.7, format: SaveFormat.JPEG }
        );
        
        const newPhoto: Photo = {
          url: '', // Will be set after upload
          uri: manipResult.uri,
          uploadedAt: new Date().toISOString(),
          description: null
        };
        
        setPhotos(prevPhotos => [...prevPhotos, newPhoto]);
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Fout', 'Er is een fout opgetreden bij het selecteren van de foto.');
    }
  };

  const takePhoto = async () => {
    if (photos.length >= 10) {
      Alert.alert('Maximum aantal foto\'s', 'Je kunt maximaal 10 foto\'s toevoegen.');
      return;
    }

    try {
      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 1,
      });

      if (!result.canceled) {
        const manipResult = await manipulateAsync(
          result.assets[0].uri,
          [],
          { compress: 0.7, format: SaveFormat.JPEG }
        );

        const newPhoto: Photo = {
          url: '', // Will be set after upload
          uri: manipResult.uri,
          uploadedAt: new Date().toISOString(),
          description: null
        };

        setPhotos(prevPhotos => [...prevPhotos, newPhoto]);
      }
    } catch (error) {
      console.error('Error taking photo:', error);
      Alert.alert('Fout', 'Er is een fout opgetreden bij het maken van de foto.');
    }
  };

  const uploadImages = async (photoUris: string[]): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (const uri of photoUris) {
      try {
        if (!auth.currentUser) {
          throw new Error('User not authenticated');
        }

        const response = await fetch(uri);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        const timestamp = Date.now();
        const randomString = Math.random().toString(36).substring(2, 8);
        const filename = `${timestamp}-${randomString}.jpg`;
        const userId = auth.currentUser.uid;
        const fullPath = `reports/${userId}/${filename}`;

        const storageRef = ref(storage, fullPath);
        const snapshot = await uploadBytes(storageRef, blob);
        const downloadURL = await getDownloadURL(snapshot.ref);
        uploadedUrls.push(downloadURL);
      } catch (error) {
        console.error('Error uploading image:', error);
        throw error;
      }
    }

    return uploadedUrls;
  };

  const getTranslationSafe = (path: string[], defaultValue: string = ''): string => {
    if (!translations) return defaultValue;
    let current: any = translations;
    for (const key of path) {
      if (!current[key]) return defaultValue;
      current = current[key];
    }
    return current;
  };

  // Custom alert function for web compatibility
  const showCustomAlert = (config: {
    title: string;
    message: string;
    onConfirm?: () => void;
    showCancel?: boolean;
  }) => {
    setAlertConfig(config);
    setShowAlert(true);
  };

  const createReport = async () => {
    // Check internet connection first
    if (!await checkConnection()) {
      return;
    }

    const validationErrors = [];
    
    if (!title.trim()) {
      validationErrors.push('Voeg een titel toe aan de melding');
    }
    
    if (!description.trim()) {
      validationErrors.push(getTranslationSafe(['createReport', 'error', 'noDescription'], 'Geef een beschrijving van het veiligheidsprobleem.'));
    }
    
    if (!problemType) {
      validationErrors.push('Selecteer een type veiligheidsprobleem');
    }

    if (!selectedLocation) {
      validationErrors.push('Selecteer een locatie');
    }

    if (validationErrors.length > 0) {
      showCustomAlert({
        title: 'Validatie Error',
        message: 'De volgende velden zijn verplicht:\n\n' + validationErrors.join('\n• '),
        onConfirm: () => setShowAlert(false)
      });
      return;
    }

    setLoading(true);
    try {
      console.log('Start creating report...');
      const groupId = await AsyncStorage.getItem('currentGroupId');
      console.log('GroupId retrieved:', groupId);

      if (!groupId) {
        console.log('No groupId found');
        setLoading(false);
        showCustomAlert({
          title: 'Login Error',
          message: 'Je bent niet meer ingelogd. Log opnieuw in om een melding te maken.',
          onConfirm: () => {
            setShowAlert(false);
            window.location.href = '/login';
          }
        });
        return;
      }

      // Check connection again before uploading photos
      if (photos.length > 0 && !await checkConnection()) {
        setLoading(false);
        return;
      }

      let photoUrls: string[] = [];
      if (photos.length > 0) {
        console.log('Starting photo upload process...');
        setUploading(true);
        try {
          const validPhotoUris = photos.map(photo => photo.uri).filter((uri): uri is string => uri !== undefined);
          console.log('Valid photo URIs:', validPhotoUris.length);
          photoUrls = await uploadImages(validPhotoUris);
          console.log('Photos uploaded successfully:', photoUrls.length);
        } catch (error) {
          console.error('Error uploading photos:', error);
          if (!isOnline) {
            showCustomAlert({
              title: 'Foto Upload Error',
              message: 'Je hebt geen internetverbinding. Wil je de melding opslaan zonder foto\'s?',
              onConfirm: async () => {
                setShowAlert(false);
                  setUploading(false);
                if (await checkConnection()) {
                  await submitReport(groupId, []);
                }
              },
              showCancel: true
            });
          } else {
            showCustomAlert({
              title: 'Foto Upload Error',
              message: 'Er is een probleem opgetreden bij het uploaden van de foto\'s. Wil je doorgaan zonder foto\'s?',
              onConfirm: async () => {
                setShowAlert(false);
                setUploading(false);
                  await submitReport(groupId, []);
              },
              showCancel: true
            });
                }
          return;
        }
        setUploading(false);
      }

      // Check connection one final time before submitting
      if (!await checkConnection()) {
        setLoading(false);
        return;
      }

      console.log('Proceeding to submit report...');
      await submitReport(groupId, photoUrls);
    } catch (error) {
      console.error('Error in createReport:', error);
      let errorMessage = 'Er is een onverwachte fout opgetreden bij het aanmaken van de melding.';
      
      if (!isOnline) {
        errorMessage = 'Je hebt geen internetverbinding. Controleer je verbinding en probeer het opnieuw.';
      }
      
      showCustomAlert({
        title: 'Fout bij maken melding',
        message: errorMessage,
        onConfirm: () => setShowAlert(false)
      });
    } finally {
      setLoading(false);
    }
  };

  const submitReport = async (groupId: string, photoUrls: string[]) => {
    if (!await checkConnection()) {
      return;
    }

    console.log('Starting submitReport...');
    const serverTime = serverTimestamp();
    const userId = auth.currentUser?.uid;
    
    console.log('Current user ID:', userId);
    
    if (!userId) {
      console.log('No user ID found');
      Alert.alert(
        'Authenticatie Error',
        'Je bent niet meer ingelogd. Log opnieuw in om een melding te maken.',
        [
          {
            text: 'OK',
            onPress: () => navigation.navigate('Login')
          }
        ]
      );
      return;
    }

    const currentTime = new Date().toISOString();
    console.log('Preparing report data...');

    const reportData = {
      title: title.trim(),
      description: description.trim(),
      photos: photoUrls.map(url => ({
        url,
        uploadedAt: currentTime,
        description: null
      })),
      photoUrl: photoUrls[0] || null,
      groupId,
      createdBy: userId,
      createdAt: serverTime,
      status: 'pending_approval',
      operationalImpact: operationalImpact.trim(),
      temporarySolution: temporarySolution.trim(),
      problemType,
      locationId: selectedLocation,
      approvedBy: null,
      approvedAt: null
    };

    console.log('Report data prepared:', { ...reportData, photos: `${photoUrls.length} photos` });

    try {
      console.log('Adding document to Firestore...');
      const docRef = await addDoc(collection(firestore, 'reports'), reportData);
      console.log('Document added successfully with ID:', docRef.id);
      
      setLoading(false);
      showCustomAlert({
        title: 'Melding succesvol aangemaakt!',
        message: 'Je melding is succesvol opgeslagen en wordt beoordeeld door een beheerder.',
        onConfirm: () => {
          setShowAlert(false);
          // Use window.location for more reliable navigation in PWA
          window.location.href = '/';
        }
      });
    } catch (error) {
      console.error('Error in submitReport:', error);
      setLoading(false);
      
      let errorMessage = 'Er is een fout opgetreden bij het opslaan van de melding.';
      
      if (error instanceof Error) {
        console.error('Error details:', error.message);
        if (error.message.includes('permission-denied')) {
          errorMessage = 'Je hebt geen rechten om een melding aan te maken. Neem contact op met je beheerder.';
        } else if (error.message.includes('network')) {
          errorMessage = 'Geen internetverbinding. Controleer je verbinding en probeer het opnieuw.';
        } else {
          errorMessage = `Er is een fout opgetreden: ${error.message}`;
        }
      }
      
      showCustomAlert({
        title: 'Fout bij opslaan',
        message: errorMessage,
        onConfirm: () => setShowAlert(false)
      });
    }
  };

  const renderProblemTypeSelector = () => {
    if (!translations) return null;

    return (
      <View style={styles.section}>
        <Text style={styles.label}>{getTranslationSafe(['createReport', 'problemType', 'label'], 'Type veiligheidsprobleem')}</Text>
        <View style={styles.pickerWrapper}>
          <Picker
            selectedValue={problemType}
            onValueChange={handleProblemTypeChange}
            style={styles.picker}
          >
            {Object.entries(translations.createReport.problemType)
              .filter(([key]) => key !== 'label')
              .map(([value, label]) => (
                <Picker.Item key={value} label={label} value={value} />
              ))}
          </Picker>
        </View>
      </View>
    );
  };

  const handleProblemTypeChange = (itemValue: string) => {
    setProblemType(itemValue as SafetyProblemType);
  };

  const renderLocationSelector = () => {
    const handleLocationChange = (itemValue: string) => {
      setSelectedLocation(itemValue);
    };

    return (
      <View style={styles.section}>
        <Text style={styles.label}>
          {getTranslationSafe(['location', 'label'], 'Locatie')}<Text style={styles.requiredStar}> *</Text>
        </Text>
        <View style={styles.pickerWrapper}>
          <Picker
            selectedValue={selectedLocation}
            onValueChange={handleLocationChange}
            style={styles.picker}
          >
            <Picker.Item 
              label={getTranslationSafe(['location', 'placeholder'], 'Selecteer een locatie')} 
              value="" 
            />
            {locations.map((location) => (
              <Picker.Item 
                key={location.id} 
                label={location.name} 
                value={location.id} 
              />
            ))}
          </Picker>
        </View>
      </View>
    );
  };

  const getTranslation = (key: string) => {
    if (!translations) return '';
    return translations[key] || key;
  };

  return (
    <View style={styles.container}>
      {!isOnline && (
        <View style={styles.offlineBanner}>
          <Text style={styles.offlineBannerText}>
            Je bent offline. Controleer je internetverbinding.
          </Text>
        </View>
      )}
      <KeyboardAvoidingView 
        style={styles.keyboardView}
        behavior={Platform.OS === "ios" ? "padding" : undefined}
        keyboardVerticalOffset={Platform.OS === "ios" ? 64 : 0}
      >
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.photoButtons}>
            <TouchableOpacity 
              style={[styles.button, photos.length >= 10 && styles.disabledButton]} 
              onPress={takePhoto}
              disabled={photos.length >= 10}
            >
              <Text style={styles.buttonText}>
                {getTranslationSafe(['createReport', 'takePhoto'], 'Foto nemen')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={[styles.button, photos.length >= 10 && styles.disabledButton]} 
              onPress={pickImage}
              disabled={photos.length >= 10}
            >
              <Text style={styles.buttonText}>
                {getTranslationSafe(['createReport', 'choosePhoto'], 'Foto kiezen')}
              </Text>
            </TouchableOpacity>
          </View>

          {photos.length > 0 && (
            <View style={styles.photosContainer}>
              {photos.map((photo, index) => (
                <View key={index} style={styles.photoContainer}>
                  <Image source={{ uri: photo.uri }} style={styles.photo} />
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => setPhotos(prevPhotos => prevPhotos.filter((_, i) => i !== index))}
                  >
                    <Text style={styles.removeButtonText}>
                      {getTranslationSafe(['createReport', 'removePhoto'], 'Foto verwijderen')}
                    </Text>
                  </TouchableOpacity>
                </View>
              ))}
              <Text style={styles.photoCount}>
                {photos.length}/10 foto's
              </Text>
            </View>
          )}

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>
              Titel
              <Text style={styles.requiredStar}> *</Text>
            </Text>
            <TextInput
              style={styles.input}
              placeholder="Korte titel van de melding"
              value={title}
              onChangeText={setTitle}
              maxLength={50}
              placeholderTextColor="#666"
            />
          </View>

          {renderProblemTypeSelector()}
          {renderLocationSelector()}

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>
              {getTranslationSafe(['createReport', 'description', 'label'], 'Beschrijving')}<Text style={styles.requiredStar}> *</Text>
            </Text>
            <TextInput
              style={styles.textArea}
              placeholder={getTranslationSafe(['createReport', 'description', 'placeholder'], 'Beschrijf het veiligheidsprobleem...')}
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={4}
              placeholderTextColor="#666"
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>
              {getTranslationSafe(['createReport', 'operationalImpact', 'label'], 'Operational Impact')}<Text style={styles.optionalText}> (optioneel)</Text>
            </Text>
            <TextInput
              style={styles.textArea}
              placeholder={getTranslationSafe(['createReport', 'operationalImpact', 'placeholder'], 'Beschrijf het operational impact...')}
              value={operationalImpact}
              onChangeText={setOperationalImpact}
              multiline
              numberOfLines={3}
              placeholderTextColor="#666"
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>

          <View style={styles.fieldContainer}>
            <Text style={styles.label}>
              {getTranslationSafe(['createReport', 'temporarySolution', 'label'], 'Tijdelijke oplossing')}<Text style={styles.optionalText}> (optioneel)</Text>
            </Text>
            <TextInput
              style={styles.textArea}
              placeholder={getTranslationSafe(['createReport', 'temporarySolution', 'placeholder'], 'Beschrijf de tijdelijke oplossing...')}
              value={temporarySolution}
              onChangeText={setTemporarySolution}
              multiline
              numberOfLines={3}
              placeholderTextColor="#666"
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>
        </ScrollView>

        <View style={styles.submitButtonContainer}>
          <TouchableOpacity
            style={[styles.submitButton, (loading || uploading) && styles.disabledButton]}
            onPress={createReport}
            disabled={loading || uploading}
          >
            {loading || uploading ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.submitButtonText}>
                {getTranslationSafe(['createReport', 'submit'], 'Melden')}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
      
      {showAlert && (
        <View style={styles.modalOverlay}>
          <View style={styles.alertContainer}>
            <Text style={styles.alertTitle}>{alertConfig.title}</Text>
            <Text style={styles.alertMessage}>{alertConfig.message}</Text>
            <View style={styles.alertButtons}>
              {alertConfig.showCancel && (
                <TouchableOpacity
                  style={[styles.alertButton, styles.alertButtonCancel]}
                  onPress={() => {
                    setShowAlert(false);
                    setLoading(false);
                    setUploading(false);
                  }}
                >
                  <Text style={styles.alertButtonText}>Annuleren</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={[styles.alertButton, styles.alertButtonConfirm]}
                onPress={() => alertConfig.onConfirm?.()}
              >
                <Text style={styles.alertButtonText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    paddingTop: 0,
  },
  keyboardView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    padding: 16,
    paddingTop: 0,
    paddingBottom: 100,
  },
  textArea: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    minHeight: 100,
    backgroundColor: '#FFFFFF',
    color: '#333',
    textAlignVertical: 'top',
  },
  photoButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    marginTop: 10,
  },
  button: {
    backgroundColor: '#005575',
    padding: 12,
    borderRadius: 8,
    flex: 0.48,
  },
  buttonText: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontWeight: '600',
  },
  photosContainer: {
    marginBottom: 16,
  },
  photoContainer: {
    marginBottom: 16,
  },
  photo: {
    width: '100%',
    height: 200,
    borderRadius: 8,
    marginBottom: 8,
  },
  removeButton: {
    padding: 8,
    backgroundColor: '#F44336',
    borderRadius: 4,
  },
  removeButtonText: {
    color: '#FFFFFF',
    textAlign: 'center',
  },
  photoCount: {
    textAlign: 'center',
    color: '#666',
    fontSize: 14,
    marginTop: 8,
  },
  submitButtonContainer: {
    padding: 16,
    paddingBottom: Platform.OS === 'ios' ? 32 : 28,
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
  },
  submitButton: {
    backgroundColor: '#005575',
    padding: 16,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  disabledButton: {
    opacity: 0.7,
  },
  submitButtonText: {
    color: '#FFFFFF',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: 16,
  },
  fieldContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  buttonGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  priorityButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
  },
  dangerButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
  },
  selectedButton: {
    backgroundColor: '#005575',
  },
  buttonGroupText: {
    color: '#666',
    fontWeight: '500',
  },
  selectedButtonText: {
    color: '#FFFFFF',
  },
  problemTypeContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  pickerWrapper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  pickerContainer: {
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    marginBottom: 16,
  },
  picker: {
    width: '100%',
    height: 50,
    color: '#333',
  },
  mobilePickerContainer: {
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    marginBottom: 16,
    overflow: 'hidden',
  },
  mobilePicker: {
    width: '100%',
    height: 50,
    color: '#333',
    backgroundColor: 'transparent',
  },
  customTypeContainer: {
    marginTop: 10,
  },
  customTypeInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  characterCount: {
    textAlign: 'right',
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  input: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    backgroundColor: '#FFFFFF',
    color: '#333',
  },
  section: {
    marginBottom: 20,
  },
  requiredStar: {
    color: '#FF0000',
    fontSize: 16,
  },
  optionalText: {
    color: '#666666',
    fontSize: 14,
    fontWeight: 'normal',
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  alertContainer: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 20,
    width: '90%',
    maxWidth: 400,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  alertTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#333',
  },
  alertMessage: {
    fontSize: 16,
    marginBottom: 20,
    color: '#666',
  },
  alertButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  alertButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 5,
    minWidth: 80,
    alignItems: 'center',
  },
  alertButtonConfirm: {
    backgroundColor: '#005575',
  },
  alertButtonCancel: {
    backgroundColor: '#666',
  },
  alertButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  offlineBanner: {
    backgroundColor: '#FF9800',
    padding: 10,
    width: '100%',
    alignItems: 'center',
  },
  offlineBannerText: {
    color: 'white',
    fontWeight: '600',
  },
}); 